/**
 * Base Entity Service Factory
 *
 * Factory function that generates standard CRUD methods for entity services.
 * Reduces duplication across customerService, siteService, and vehicleService.
 */

import { apiService } from '../api/apiService'
import { errorService } from '../error/errorService'

/**
 * Create base entity service methods
 * @param {Object} config - Service configuration
 * @param {Object} config.endpoints - Endpoint functions (getAll, getById)
 * @param {string} config.entityName - Entity name for error messages (e.g., 'customer', 'site', 'vehicle')
 * @param {string} config.entityType - Entity type for normalization (e.g., 'customers', 'sites', 'vehicles')
 * @param {Array<string>} config.paramKeys - Additional parameter keys to include in getAll (e.g., ['customer_id', 'site_id'])
 * @returns {Object} Service object with getAll and getById methods
 */
export function createBaseEntityService({ endpoints, entityName, entityType, paramKeys = [] }) {
  return {
    /**
     * Retrieve all entities
     * @param {object} options - Additional options (skip, limit, sort, search, and paramKeys)
     * @returns {Promise<object>} Paginated list of entities
     */
    async getAll(options = {}) {
      try {
        // Build params object with common options and entity-specific paramKeys
        const params = {
          skip: options.skip || 0,
          limit: options.limit || 100,
          sort: options.sort,
          search: options.search,
        }

        // Add entity-specific params
        paramKeys.forEach((key) => {
          if (options[key] !== undefined) {
            params[key] = options[key]
          }
        })

        return await apiService.getNormalized(
          endpoints.getAll,
          {
            params,
          },
          entityType
        )
      } catch (error) {
        errorService.logError(error, {
          context: `${entityName} service - getAll`,
          options,
        })
        throw error
      }
    },

    /**
     * Retrieve a specific entity by ID
     * @param {string} id - Entity ID
     * @param {object} options - Additional options
     * @returns {Promise<object>} Entity data
     */
    async getById(id, options = {}) {
      try {
        return await apiService.getSingleNormalized(endpoints.getById(id), options, entityName)
      } catch (error) {
        errorService.logError(error, {
          context: `${entityName} service - getById`,
          [`${entityName}Id`]: id,
          options,
        })
        throw error
      }
    },
  }
}
